import React, { useState } from "react";
import { ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { reApi, errMsg } from "@/lib/residentApi";
import { ResidentPicker } from "@/components/nds/windows/resident/ResidentPicker";
import IntakeEditor from "@/components/nds/windows/resident/IntakeEditor";
import { inputCls, GoldButton, GhostButton } from "@/components/nds/windows/resident/ui";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";
import { useAuth } from "@/lib/auth";
import QuestionnaireTab from "@/components/nds/windows/questionnaire/QuestionnaireTab";

function NewResident({ onCreated }) {
  const [f, setF] = useState(null);
  if (!f) return <GoldButton data-testid="re-intake-new" onClick={() => setF({ first_name: "", last_name: "", intake_date: new Date().toISOString().slice(0, 10) })} className="ml-auto"><Plus className="h-3 w-3" /> New resident</GoldButton>;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const create = async () => {
    try { const r = await reApi.post("/residents", f); toast.success("Resident created — complete the intake"); onCreated(r); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="re-intake-new-form" className="w-full flex flex-wrap gap-2 p-3 border border-nex-gold/30 rounded-sm">
      <input placeholder="First name" value={f.first_name} onChange={set("first_name")} data-testid="re-intake-new-first" className={`${inputCls} !w-40`} />
      <input placeholder="Last name" value={f.last_name} onChange={set("last_name")} data-testid="re-intake-new-last" className={`${inputCls} !w-40`} />
      <input type="date" value={f.intake_date} onChange={set("intake_date")} data-testid="re-intake-new-date" className={`${inputCls} !w-40`} />
      <GoldButton data-testid="re-intake-new-submit" onClick={create} disabled={!f.first_name.trim() || !f.last_name.trim()}>Create &amp; start intake</GoldButton>
      <GhostButton data-testid="re-intake-new-cancel" onClick={() => setF(null)}>Cancel</GhostButton>
    </div>
  );
}

const TABS = [["intake", "Intake (staff)"], ["questionnaire", "Referral questionnaire"]];

export default function ReIntakeFormContent() {
  const u = useAuth().user;
  const partner = u?.role === "partner_user" && !(u?.role_title === "Resident Leader" && u?.card_access?.cap_re_intake === "edit");  // leaders with Edit on Intake work it like staff
  const [tab, setTab] = useState(partner ? "questionnaire" : "intake");
  return (
    <div data-testid="re-intake-content" className="h-full overflow-y-auto p-5 space-y-4">
      {!partner && <div className="flex flex-wrap gap-2" role="tablist">{TABS.map(([k, l]) => (
        <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`re-intake-tab-${k}`}
          className={`px-3 py-1.5 rounded-sm border font-mono text-[10px] tracking-[0.18em] uppercase ${tab === k ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/10 text-nex-muted hover:text-nex-silver"}`}>{l}</button>))}</div>}
      {tab === "questionnaire" ? <QuestionnaireTab /> : <StaffIntake />}
    </div>
  );
}

function StaffIntake() {
  const [res, setRes] = useState(null);
  const [nonce, setNonce] = useState(0);
  return (
    <>
      {res ? (
        <>
          <button type="button" onClick={() => { setRes(null); setNonce((n) => n + 1); }} data-testid="re-intake-back" className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.22em] uppercase text-nex-gold"><ArrowLeft className="h-3.5 w-3.5" /> Residents</button>
          <div className="flex items-center gap-3"><h3 data-testid="re-intake-resident" className="text-lg font-semibold text-nex-silver">{res.first_name} {res.last_name}</h3><OpenResidentButton residentId={res.id} name={`${res.first_name} ${res.last_name}`} /></div>
          <IntakeEditor resident={res} />
        </>
      ) : <ResidentPicker key={nonce} prefKey="intake" filterIntake onPick={setRes} testid="re-intake-picker" extra={<NewResident onCreated={setRes} />} />}
    </>
  );
}
